import "../../css/components/inview-animate.less";

const DEFAULTS = {
    selector: "[data-animate]",
    rootMargin: "0px 0px -10% 0px", // reveal a bit before fully in view
    threshold: 0.1,
    once: true,
};

/**
 * Reveals elements when they enter the viewport.
 * Supports per-element overrides:
 *  - data-delay="0.5s" or "500ms"
 *  - data-duration="0.8s" or "800ms"
 */
export function initInViewAnimations(options = {}) {
    const cfg = { ...DEFAULTS, ...options };

    // Respect reduced motion automatically (CSS also handles this)
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return () => { };

    const els = Array.from(document.querySelectorAll(cfg.selector));
    if (els.length === 0) return () => { };

    // Fallback: if IO not supported, reveal immediately
    if (!("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-inview"));
        return () => { };
    }

    // Apply per-element transition variables from attributes
    for (const el of els) {
        const delay = el.getAttribute("data-delay");
        const duration = el.getAttribute("data-duration");

        if (delay) el.style.setProperty("--anim-delay", normalizeTime(delay));
        if (duration) el.style.setProperty("--anim-duration", normalizeTime(duration));
    }

    const io = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;

                const el = entry.target;
                el.classList.add("is-inview");

                if (cfg.once) io.unobserve(el);
            }
        },
        { rootMargin: cfg.rootMargin, threshold: cfg.threshold }
    );

    els.forEach((el) => io.observe(el));

    return () => io.disconnect();
}

function normalizeTime(value) {
    // Accept "0.5s", ".5s", "500ms", "500"
    const v = String(value).trim();
    if (v.endsWith("ms") || v.endsWith("s")) return v;
    // assume milliseconds if just a number
    return `${Number(v)}ms`;
}
